import React, { useState, useEffect, useRef } from "react";
import { Offcanvas, Badge, Input } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import DynamicTableWrapper from "./DynamicTableWrapper";
import {
  DynamicTableHelper,
  useTableHook,
  DateHelper,
} from "@workspace/common";
import {
  REPORT_INITIAL_OPTIONS,
  REPORT_MANDATORY_OPTIONS,
} from "./ReportConstants";
import {
  REPORT_STAGES,
  REPORT_CATEGORY,
  REPORTS_NAME,
  RECRUITER_PARAMS,
  CURRENT_ENGAGEMENTS_PARAMS,
  FOD_PARAMS,
} from "../UniversalReportConstant";
import { fetchJobUniversalReportListing } from "../../../store/actions";
import {
  TOS_ACCEPTED_DECLINED,
  PREPARE_TOS,
} from "@workspace/job/src/components/JobOverview/JobOverviewConstants";
import { JOB_STAGE_STATUS } from "@workspace/job/src/components/JobListing/JobListingConstants";

function ReportOffcanvas({
  isReportOffcanvasOpen,
  setIsReportOffcanvasOpen,
  selectedItem,
  filters,
  customView,
  defaultCustomView,
  bubbleExternalFilter,
}) {
  // Dispatch & Selector
  const dispatch = useDispatch();
  const reportListing = useSelector(
    (state) => state?.ReportReducer?.jobUniversalReportListing
  );

  // Table Config
  const [tableConfig, setTableConfig] = useState(null);

  // Custom Renders
  const customRenderList = [
    {
      names: ["job.jobSubmissionData.jobTitle"],
      render: (data) => (
        <div>
          <Link
            className="text-decoration-underline"
            onClick={() => {
              const win = window.open(
                `/jobs/${data?.job?.id}/snapshot`,
                "_blank"
              );
              win.focus();
            }}
            style={{ color: "#0a56ae" }}
          >
            {data?.job?.jobSubmissionData?.jobTitle}
          </Link>
        </div>
      ),
    },
    {
      names: ["job.jobSubmissionData.jobId"],
      render: (data) => (
        <div>
          <Link
            className="text-decoration-underline"
            onClick={() => {
              const win = window.open(
                `/jobs/${data?.job?.id}/snapshot`,
                "_blank"
              );
              win.focus();
            }}
            style={{ color: "#0a56ae" }}
          >
            {data?.job?.jobSubmissionData?.jobId
              ? data?.job?.jobSubmissionData?.jobId
              : "-"}
          </Link>
        </div>
      ),
    },
    {
      names: ["candidate.candidateSubmissionData.firstName"],
      render: (data) => (
        <div>
          <a
            className="text-decoration-underline cursor-pointer"
            onClick={() => {
              const win = window.open(
                `/candidates/${data?.candidate?.id}/snapshot`,
                "_blank"
              );
              win.focus();
            }}
            style={{ color: "#0a56ae" }}
          >
            {data?.candidate?.candidateSubmissionData?.firstName}{" "}
            {data?.candidate?.candidateSubmissionData?.lastName}
          </a>
        </div>
      ),
    },
    {
      names: ["subStepName"],
      render: (data) => (
        <Badge color={getBadgeColour(data?.timeline)}>
          {data?.subStepName ? data?.subStepName : "-"}
        </Badge>
      ),
    },
    {
      names: ["job.jobSubmissionData.dateOpen"],
      render: (data) => (
        <div>
          {calculateAgeing(data?.job?.jobSubmissionData?.dateOpen)} Days
        </div>
      ),
    },
    {
      names: ["updatedAt", "createdAt"],
      render: (data, opt) =>
        DateHelper.formatDateStandard2(
          DynamicTableHelper.getDynamicNestedResult(data, opt.value) || "-"
        ),
    },
  ];

  // Calculate Ageing
  const calculateAgeing = (createdAt) => {
    const currentDate = new Date();
    const createdDate = new Date(createdAt);
    const ageingValueMS = currentDate - createdDate;
    const diffDays = Math.floor(ageingValueMS / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get Profile Badge Colour
  const getBadgeColour = (obj) => {
    const rejectedStep = findRejectedStepLatestDate(obj);
    if (rejectedStep !== null) {
      return "danger";
    } else {
      return "success";
    }
  };

  // Find Rejected Step
  const findRejectedStep = (obj) => {
    // Each key value is now an array. Flatten the array of object and look for the status
    for (const key in obj) {
      const array = obj[key];
      for (const item of array) {
        if (item.status === "REJECTED" || item.status === "WITHDRAWN") {
          return { [key]: item };
        }
      }
    }
    return null;
  };

  const findRejectedStepLatestDate = (obj) => {
    // Each key value is now an array. Flatten the array of object and look for the status
    for (const key in obj) {
      const array = obj[key];
      const latestItem = array.reduce((prev, current) =>
        prev.date > current.date ? prev : current
      );
      if (
        latestItem.status === JOB_STAGE_STATUS.REJECTED ||
        latestItem.status === JOB_STAGE_STATUS.WITHDRAWN
      ) {
        // TOS CHECK
        // This is to check if a new set of TOS has started
        if (key === TOS_ACCEPTED_DECLINED) {
          // Check if prepare TOS has a later date
          const prepareTOS = obj[PREPARE_TOS];
          const latestPrepareTOS = prepareTOS.reduce((prev, current) =>
            prev.date > current.date ? prev : current
          );
          if (latestPrepareTOS.date > latestItem.date) {
            return null;
          }
        }
        return { [key]: latestItem };
      }
    }
    return null;
  };

  // Setup
  const generateReportConfig = (customConfig) => {
    return [
      {
        header: "#",
        name: "indexing",
        sort: false,
        render: (data, index) => (
          <div className="d-flex column-gap-2">
            {pageInfo?.currentPage * pageInfo?.pageSize + (index + 1)}.
          </div>
        ),
      },
      {
        header: (
          <div className="form-check px-0">
            <Input
              className="form-check-input"
              type="checkbox"
              id="checkbox"
              checked={
                activeRow?.length > 0 && activeRow?.length === tableData?.length
              }
              onChange={(e) => selectAllRows(e?.target?.checked)}
            />
          </div>
        ),
        name: "checkbox",
        sort: false,
        render: (data) => {
          return (
            <div className="form-check px-0">
              <Input
                className="form-check-input"
                type="checkbox"
                name="chk_child"
                checked={activeRow?.includes(parseInt(data?.id))}
                onChange={(e) => handleRowCheck(data?.id, e.target.checked)}
              />
            </div>
          );
        },
      },
      ...customConfig,
    ];
  };

  // Table Hooks
  const {
    pageRequest,
    pageRequestSet,
    pageInfo,
    setPageInfoData,
    search,
    setSearch,
    customConfig,
    setCustomConfigData,
    setTableData,
    tableData,
    activeRow,
    setActiveRow,
    handleRowCheck,
    selectAllRows,
  } = useTableHook(
    {
      page: 0,
      pageSize: 50,
      sortBy: null,
      sortDirection: "asc",
      searchTerm: null,
      searchFields: DynamicTableHelper.generateSeachFieldArray([]),
    },
    REPORT_MANDATORY_OPTIONS,
    REPORT_INITIAL_OPTIONS,
    customRenderList,
    generateReportConfig
  );

  const hasPageRendered = useRef(false);
  const [jobStep, setJobStep] = useState(null);

  const getJobStep = (reportCategory, reportName) => {
    switch (reportCategory) {
      // case REPORT_CATEGORY.RECRUITER:
      //   switch (reportName) {
      //     case REPORTS_NAME[REPORT_CATEGORY.RECRUITER].ACTIVE_RECRUITERS_DISTINCT:
      //       return RECRUITER_PARAMS.ACTIVE_RECRUITERS_DISTINCT;
      //     case REPORTS_NAME[REPORT_CATEGORY.RECRUITER].ACTIVE_RECRUITERS:
      //       return RECRUITER_PARAMS.ACTIVE_RECRUITERS;
      //   }
      // case REPORT_CATEGORY.JOBS_ACTIONS:
      //   switch (reportName) {
      //     case REPORTS_NAME[REPORT_CATEGORY.JOBS_ACTIONS].ACTION_TAKEN:
      //       return ACTION_PARAMS.ACTION_TAKEN;
      //   }
      case REPORT_CATEGORY.PROFILES:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].FOD_TOTAL:
            return FOD_PARAMS.FOD_TOTAL;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES]
            .ACTIVE_RECRUITERS_DISTINCT:
            return RECRUITER_PARAMS.ACTIVE_RECRUITERS_DISTINCT;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].ACTIVE_RECRUITERS:
            return RECRUITER_PARAMS.ACTIVE_RECRUITERS;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].CURRENT_ENGAGEMENTS:
            return CURRENT_ENGAGEMENTS_PARAMS.CURRENT_ENGAGEMENTS;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].TAG:
            return REPORT_STAGES.TAGGED;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].ASSOCIATED:
            return REPORT_STAGES.ASSOCIATED;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].SUBMITTED_TO_SALES:
            return REPORT_STAGES.SUBMIT_TO_SALES;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].SUBMITTED_TO_CLIENT:
            return REPORT_STAGES.SUBMIT_TO_CLIENT;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].PROFILE_FEEDBACK_PENDING:
            return REPORT_STAGES.PROFILE_FEEDBACK;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].CANDIDATE_WITHDRAWN:
            return REPORT_STAGES.PROFILE_WITHDRAWN;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].PROFILE_REJECTED_BY_SALES:
            return REPORT_STAGES.SUBMIT_TO_SALES_REJECTED;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES]
            .PROFILE_REJECTED_BY_CLIENT:
            return REPORT_STAGES.SUBMIT_TO_CLIENT_REJECTED;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].NO_SUBMISSIONS:
            return REPORT_STAGES.NO_SUBMISSION;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES].REQUIREMENTS_WORKED_ON:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES]
            .PROFILE_SUBMITTED_TO_INTERVIEWS_HAPPENED:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.PROFILES]
            .INTERVIEW_HAPPENED_TO_SELECTED:
            return;
          default:
            return;
        }
      case REPORT_CATEGORY.INTERVIEWS:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].SCHEDULED_INTERVIEW:
            return REPORT_STAGES.INTERVIEW_SCHEDULED;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].RESCHEDULE_INTERVIEW:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].INTERVIEW_HAPPENED:
            return REPORT_STAGES.INTERVIEW_HAPPENED;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS]
            .INTERVIEW_BACKOUT_BY_CANDIDATE:
            return REPORT_STAGES.INTERVIEW_BACK_OUT;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS]
            .INTERVIEW_REJECTED_BY_CLIENT:
            return REPORT_STAGES.INTERVIEW_REJECTED;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].UPCOMING_INTERVIEWS:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].UNASSIGNED_REQUIREMENTS:
            return;
          case REPORTS_NAME[REPORT_CATEGORY.INTERVIEWS].NEW_JOB_TO_SELECTED:
            return REPORT_STAGES.INTERVIEW_FEEDBACK_PENDING;
          default:
            return;
        }
      case REPORT_CATEGORY.CONDITIONAL_OFFERS:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.CONDITIONAL_OFFERS].PREPARED:
            return REPORT_STAGES.CONDITIONAL_OFFER_PREPARED;
          case REPORTS_NAME[REPORT_CATEGORY.CONDITIONAL_OFFERS].RELEASED:
            return REPORT_STAGES.CONDITIONAL_OFFER_RELEASED;
          case REPORTS_NAME[REPORT_CATEGORY.CONDITIONAL_OFFERS].ACCEPTED:
            return REPORT_STAGES.CONDITIONAL_OFFER_ACCEPTED;
          case REPORTS_NAME[REPORT_CATEGORY.CONDITIONAL_OFFERS].REJECTED:
            return REPORT_STAGES.CONDITIONAL_OFFER_REJECTED;
          default:
            return;
        }
      case REPORT_CATEGORY.TOS:
        switch (reportName) {
          case REPORTS_NAME[REPORT_CATEGORY.TOS].PREPARED:
            return REPORT_STAGES.TOS_PREPARED;
          case REPORTS_NAME[REPORT_CATEGORY.TOS].APPROVED:
            return REPORT_STAGES.TOS_APPROVED;
          case REPORTS_NAME[REPORT_CATEGORY.TOS].REJECTED:
            return REPORT_STAGES.TOS_REJECTED;
          default:
            return;
        }
      default:
        return;
    }
  };

  // Set Job Step
  useEffect(() => {
    if (selectedItem) {
      setJobStep(getJobStep(selectedItem?.key, selectedItem.value));
    }
  }, [selectedItem]);

  useEffect(() => {
    if (
      jobStep &&
      jobStep !== "" &&
      filters !== null &&
      isReportOffcanvasOpen === true &&
      bubbleExternalFilter !== null &&
      pageRequest?.searchFields?.length > 0
    ) {
      dispatch(
        fetchJobUniversalReportListing(
          DynamicTableHelper.cleanPageRequest({
            ...pageRequest,
            jobStep,
            filters: bubbleExternalFilter,
          })
        )
      );
    }
  }, [JSON.stringify(pageRequest), jobStep, customView, bubbleExternalFilter]);

  useEffect(() => {
    if (reportListing) {
      setPageInfoData(reportListing);
      setTableData(reportListing?.jobs);
    }
  }, [reportListing, jobStep]);

  useEffect(() => {
    if (tableConfig) {
      const newConfig = generateReportConfig(customConfig);
      newConfig.forEach((item, index) => {
        const oldConfig = tableConfig.find(
          (oldItem) => oldItem?.name === item?.name
        );
        if (!oldConfig) return;
        if (oldConfig?.expand) {
          newConfig[index].expand = true;
        } else {
          newConfig[index].expand = false;
        }
      });
      setTableConfig(newConfig);
    } else {
      setTableConfig(generateReportConfig(customConfig));
    }
  }, [customConfig, pageInfo, activeRow, tableData]);

  // Check if page has re-rendered
  useEffect(() => {
    if (hasPageRendered.current) {
      pageRequestSet.setPage(0);
    }
    hasPageRendered.current = true;
  }, []);

  return (
    <React.Fragment>
      <Offcanvas
        backdrop={false}
        direction="bottom"
        isOpen={isReportOffcanvasOpen}
        toggle={() => {
          if (isReportOffcanvasOpen) {
            setJobStep(null);
            pageRequestSet.setSearchFields([]);
          }
          setIsReportOffcanvasOpen(!isReportOffcanvasOpen);
        }}
        style={{ height: "72vh", borderTop: "1px solid lightgray" }}
      >
        <div className="offcanvas-body" style={{ overflowY: "hidden" }}>
          <DynamicTableWrapper
            data={reportListing?.jobs ?? []}
            config={tableConfig}
            pageInfo={pageInfo}
            pageRequest={pageRequest}
            pageRequestSet={pageRequestSet}
            search={search}
            setSearch={setSearch}
            // optGroup={candidatesFields}
            setCustomConfigData={setCustomConfigData}
            header="Universal Report"
            activeRow={activeRow}
            setActiveRow={setActiveRow}
            setTableConfig={setTableConfig}
            jobStep={jobStep}
            isReportOffcanvasOpen={isReportOffcanvasOpen}
            setIsReportOffcanvasOpen={setIsReportOffcanvasOpen}
            customView={customView}
            defaultCustomView={defaultCustomView}
            bubbleExternalFilter={bubbleExternalFilter}
          />
        </div>
      </Offcanvas>
    </React.Fragment>
  );
}

export default ReportOffcanvas;
